/**
 * followups API client — Story 7.14b → 9.7 refactor.
 *
 * Typed wrappers around `apiClient.{get,post}` for the `/admin/followups*`
 * endpoints. Types vêm do shared package `@sales-agent/admin-schemas/followups`
 * (Story 9.7) — single source of truth com backend; replicação manual
 * removida.
 *
 * Convention: lib/followups.ts (singular, no subdir) — same pattern as
 * `lib/callbacks.ts` and `lib/promptAssistant.ts`.
 *
 * RBAC reminder (enforced backend-side):
 *   - GET list / GET detail / GET aggregate → viewer | operator | admin
 *   - POST conversations/:id/followups/retry → operator | admin
 *
 * Phone is always returned masked from the backend (`maskedPhone`); for admin
 * role the detail endpoint returns full digits in the same field.
 */
import type {
  FollowupExecutionStatus,
  FollowupListItemT,
  FollowupListResponseT,
  FollowupDetailT,
  FollowupAggregate24hResponseT,
} from '@sales-agent/admin-schemas/followups';

import { apiClient } from './api';

// Re-export shared types under admin-friendly aliases — preserva contratos
// com Followups.tsx + FollowupDetail.tsx + tests.
export type { FollowupExecutionStatus };
export type FollowupListItem = FollowupListItemT;
export type FollowupListResponse = FollowupListResponseT;
export type FollowupDetail = FollowupDetailT;
export type FollowupAggregate24h = FollowupAggregate24hResponseT;

// ─── Admin-only types (não vêm do backend; concept exclusivo da UI) ──────

export interface FollowupListFilters {
  statuses: FollowupExecutionStatus[];
  campaignId: string;
  from: string;
  to: string;
  customerPhone: string;
}

export const EMPTY_FOLLOWUP_FILTERS: FollowupListFilters = {
  statuses: [],
  campaignId: '',
  from: '',
  to: '',
  customerPhone: '',
};

export const ALL_FOLLOWUP_STATUSES: FollowupExecutionStatus[] = [
  'sent',
  'failed',
  'skipped_window',
  'skipped_opted_out',
  'skipped_24h_gate',
];

/**
 * Serialize filters + pagination into the querystring expected by
 * `GET /admin/followups`. `from`/`to` from a `datetime-local` input are
 * normalised to ISO 8601 UTC.
 */
export function toFollowupQueryString(
  f: FollowupListFilters,
  page: number,
  pageSize: number,
): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  if (f.statuses.length > 0) params.set('status', f.statuses.join(','));
  if (f.campaignId) params.set('campaign_id', f.campaignId);
  if (f.from) params.set('from', new Date(f.from).toISOString());
  if (f.to) params.set('to', new Date(f.to).toISOString());
  if (f.customerPhone) params.set('customer_phone', f.customerPhone);
  return params.toString();
}

export const followupsApi = {
  list: (qs: string) => apiClient.get<FollowupListResponse>(`/admin/followups?${qs}`),
  get: (id: string) => apiClient.get<FollowupDetail>(`/admin/followups/${id}`),
  aggregate24h: () => apiClient.get<FollowupAggregate24h>('/admin/followups/aggregate/24h'),
  retry: (conversationId: string) =>
    apiClient.post(`/admin/conversations/${conversationId}/followups/retry`),
};
